import { useRequest } from 'features/request/useRequest';
import type { SelectedFilters } from 'uikit';

import { debounce } from 'debounce';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getCatalogsGql } from '../catalogListQueries';
import type { IQueryOrder, IQueryWhere } from '../helpers';
import { getWhere, sortCatalogListQueryOrder } from '../helpers';

export const useCatalogListData = () => {
    const dispatch = useDispatch();

    const [searchString, setSearchString] = useState('');
    const [queryWhere, setQueryWhere] = useState<IQueryWhere>(undefined);
    const [queryOrder, setQueryOrder] = useState<IQueryOrder>([{ updatedAt: 'DESC' }]);

    const variables = useMemo(
        () => ({
            where: getWhere(queryWhere),
            order: queryOrder,
        }),
        [queryOrder, queryWhere],
    );

    const handleSearch = useCallback(({ value }) => {
        setQueryWhere((prevFilters) => ({
            ...prevFilters,
            searchString: value,
        }));
    }, []);

    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

    const handleChangeSearchString = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const handleClearSearch = useCallback(() => {
        if (searchString) {
            handleChangeSearchString({ value: undefined });
        }
    }, [handleChangeSearchString, searchString]);

    const handleOnFiltersUpdated = useCallback((selectedFilters: SelectedFilters) => {
        setQueryWhere((prevFilters: any) => ({ ...prevFilters, ...selectedFilters }));
    }, []);

    const handleOnSortUpdated = useCallback((sortParams) => {
        setQueryOrder(sortCatalogListQueryOrder(sortParams));
    }, []);

    const [catalogListData, catalogListStatus, , catalogListRequest] = useRequest({
        requestId: 'catalogList',
        query: getCatalogsGql,
        variables,
    });

    const rowsWithId = useMemo(
        () =>
            catalogListData?.catalogs?.items?.map((item) => ({ ...item, id: item.uniqueId, idCatalogItem: item.id })) ??
            [],
        [catalogListData?.catalogs?.items],
    );

    const totalRowCount = catalogListData?.catalogs?.totalCount || 0;

    const getCatalogList = useCallback(() => {
        dispatch(catalogListRequest(variables));
    }, [catalogListRequest, dispatch, variables]);

    return {
        tableData: rowsWithId,
        fetchingStatus: catalogListStatus,
        searchString,
        handleChangeSearchString,
        handleClearSearch,
        handleOnFiltersUpdated,
        handleOnSortUpdated,
        totalRowCount,
        getCatalogList,
    };
};
